import { Creature } from "./creature.js";
import { COPY } from "./copy.js";
import { familyClasses, familyLook } from "./accent.js";

/** What the child chose to do after watching a game demonstrate itself. */
export type TutorialChoice = "replay" | "start";

/**
 * The prompt a game holds on after demonstrating itself.
 *
 * **A bar, not a dialog, and that reversal came from watching it fail.** The first version was a
 * centred modal over a dimmed backdrop, and it covered the board at exactly the moment the child
 * would have looked at it: the demonstration finished, the finished shape appeared, and half a
 * second later a card slid over the top of it. What the game had just shown was hidden by the thing
 * asking whether they had seen it. A bottom bar leaves the whole board visible.
 *
 * **And it can be replayed.** A demonstration that plays once, at a speed somebody else chose, is a
 * demonstration a child who blinked has lost for good — and the alternative for them was to guess
 * their way through a game whose rule they never saw. "Watch again" costs one button.
 *
 * **Why it may contain text at all.** The rule that nothing on these surfaces may be written down
 * (v2 §15) is about the STIMULUS: language inside a measured trial means reading ability leaks into
 * what the trial measures, and there are no local norms to correct for that. This sits between the
 * demonstration and the first scored trial, and everything it says only confirms what the child was
 * already shown. A child who cannot read it has been told the rule by watching, and the replay
 * button is a picture as much as a word.
 */
export function TutorialPrompt({
  family,
  onChoose,
}: {
  family: string;
  onChoose: (choice: TutorialChoice) => void;
}) {
  const look = familyLook(family);
  const accent = familyClasses(family);

  return (
    <div
      role="group"
      // No backdrop and no dimming: the board above stays exactly as the demonstration left it.
      // The keyframes are in app.css; the padding carries the home-indicator inset, which has no
      // utility because it is an environment value rather than a scale step.
      className="kd-sheet-in pb-safe-md absolute inset-x-0 bottom-0 z-10 grid justify-items-center gap-sm rounded-t-xl bg-card p-md shadow-raised"
    >
      <div className="flex items-center gap-sm">
        {/* Small: the board is the thing worth looking at, not this. */}
        <span className={`grid size-11 shrink-0 place-items-center rounded-pill ${accent.disc}`}>
          <Creature kind={look.creature} accent={look.accent} size={32} still />
        </span>
        <p className="text-small text-ink">{COPY.child.tutorialBody}</p>
      </div>

      <div className="flex w-full gap-sm">
        <button
          onClick={() => onChoose("replay")}
          className={`min-h-touch min-w-[116px] shrink-0 rounded-pill text-small font-bold text-ink ${accent.chip}`}
        >
          {/* The symbol carries it for a child who cannot read the label beside it. */}
          <span aria-hidden className="me-1.5">
            ↻
          </span>
          {COPY.child.tutorialReplay}
        </button>

        <button
          onClick={() => onChoose("start")}
          autoFocus
          className={`min-h-touch flex-1 rounded-pill text-heading text-ink ${accent.bg} ${accent.shadow}`}
        >
          {COPY.child.tutorialStart}
        </button>
      </div>
    </div>
  );
}
